import { create } from 'zustand';
import {
  UNREGISTERED_PATIENT_MESSAGE,
  deactivatePatient,
  getPatientRechargeQuota,
  getCEP,
  getPatients,
  isUnregisteredPortalHomecarePatient,
  savePatient,
} from '../../../services/patients/patientsService';
import { PatientDataSource } from '../../../datasources/patients/PatientDataSource';

const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

const isEmpty = (value) => String(value ?? '').trim() === '';

const normalizeSap = (value) => onlyDigits(value).replace(/^0+/, '');

const isEnterpriseSap = (value) => {
  const sap = normalizeSap(value);
  return sap.startsWith('1') || sap.startsWith('6');
};

const isValidCpf = (value) => {
  const digits = onlyDigits(value);
  return digits.length === 11 && !/^(\d)\1+$/.test(digits);
};

const isValidCnpj = (value) => onlyDigits(value).length === 14;

const isValidCep = (value) => onlyDigits(value).length === 8;

const isValidPhone = (value) => {
  const digits = onlyDigits(value);
  return digits.length >= 10 && digits.length <= 12;
};

const normalizePatientStatusForSap = (status, sap) => {
  const value = String(status ?? '').trim();

  if (!normalizeSap(sap)) {
    return 'Solicitado';
  }

  return value.toLowerCase() === 'solicitado' ? 'Ativo' : value || 'Ativo';
};

const getBirthDateError = (value) => {
  const text = String(value ?? '').trim();
  if (!text) return '';

  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const brMatch = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (!isoMatch && !brMatch) {
    return 'Informe uma data de nascimento válida.';
  }

  const year = Number(isoMatch?.[1] ?? brMatch?.[3]);
  const month = Number(isoMatch?.[2] ?? brMatch?.[2]);
  const day = Number(isoMatch?.[3] ?? brMatch?.[1]);
  const date = new Date(year, month - 1, day);
  const isValidCalendarDate =
    year > 0 &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;

  if (!isValidCalendarDate) {
    return 'Informe uma data de nascimento válida.';
  }

  const today = new Date();
  const inputDate = year * 10000 + month * 100 + day;
  const currentDate =
    today.getFullYear() * 10000 +
    (today.getMonth() + 1) * 100 +
    today.getDate();

  return inputDate > currentDate
    ? 'Informe uma data de nascimento válida.'
    : '';
};

let latestPatientsRequest = 0;

const validatePatientBeforeSave = (state) => {
  const errors = [];
  const allowInvalidCpf = isEnterpriseSap(state.sap);

  if (isEmpty(state.name)) {
    errors.push('Informe o nome completo do paciente.');
  }

  if (isEmpty(state.cpf) && !allowInvalidCpf) {
    errors.push('Informe o CPF do paciente.');
  } else if (
    !isEmpty(state.cpf) &&
    !isValidCpf(state.cpf) &&
    !allowInvalidCpf
  ) {
    errors.push('CPF deve conter 11 dígitos válidos.');
  }

  if (isEmpty(state.cnpj)) {
    errors.push('Selecione a empresa/CNPJ.');
  } else if (!isValidCnpj(state.cnpj)) {
    errors.push('CNPJ deve conter 14 dígitos.');
  }

  if (isEmpty(state.cellphone)) {
    errors.push('Informe o telefone/celular do paciente.');
  } else if (!isValidPhone(state.cellphone)) {
    errors.push(
      'Telefone/celular deve conter DDD e ter entre 10 e 12 dígitos.'
    );
  }

  if (!isEmpty(state.phoneNumber) && !isValidPhone(state.phoneNumber)) {
    errors.push('Telefone 2 deve conter DDD e ter entre 10 e 12 dígitos.');
  }

  if (isEmpty(state.cep)) {
    errors.push('Informe o CEP.');
  } else if (!isValidCep(state.cep)) {
    errors.push('CEP deve conter 8 dígitos.');
  }

  if (isEmpty(state.city)) {
    errors.push('Informe a cidade.');
  }

  if (isEmpty(state.uf)) {
    errors.push('Informe a UF.');
  } else if (String(state.uf).trim().length !== 2) {
    errors.push('UF deve conter 2 letras.');
  }

  if (isEmpty(state.address)) {
    errors.push('Informe o endereço.');
  }

  if (isEmpty(state.number)) {
    errors.push('Informe o número.');
  }

  const birthDateError =
    state.newPat && isEmpty(state.birthDate)
      ? 'Informe a data de nascimento do paciente.'
      : getBirthDateError(state.birthDate);
  if (birthDateError) {
    errors.push(birthDateError);
  }

  // SAP, complemento e email são opcionais.
  return errors;
};

export const usePatientsStore = create((set, get) => ({
  id: 0,
  name: '',
  email: '',
  sap: '',
  cpf: '',
  birthDate: '',
  responsibleName: '',
  cep: '',
  city: '',
  uf: '',
  number: '',
  phoneNumber: '',
  responsiblePhoneNumber: '',
  address: '',
  addressLine2: '',
  neighborhood: '',
  cnpj: '',
  companyName: '',
  cellphone: '',
  status: '',
  newPat: null,
  homeVoltage: '',
  diagnosisHypothesis: '',
  onPaymentList: null,
  patients: [],
  currentSelectPatientCPF: '',
  currentSelectPatientSap: '',
  fetchTotalItems: 0,
  kpiSelectedPatient: null,
  error: null,
  infoMessage: null,
  patientAutofilledFromDatalake: false,
  additionalInformation: [],
  additionalInformationList: [],
  searchTerm: '',

  setAdditionalInformation: (additionalInformation) =>
    set({ additionalInformation }),

  getAdditionalInformationList: async () => {
    const data = await new PatientDataSource().getAdditionalInformation();
    set({ additionalInformationList: data });
  },

  setId: (id) => set({ id }),
  setCurrentSelectPatientCPF: (currentSelectPatientCPF) =>
    set({ currentSelectPatientCPF }),
  setCurrentSelectPatientSap: (currentSelectPatientSap) =>
    set({ currentSelectPatientSap }),
  setName: (name) => set({ name }),
  setEmail: (email) => set({ email }),
  setSap: (sap) => set({ sap }),
  setFetchTotalItems: (fetchTotalItems) => set({ fetchTotalItems }),
  setCpf: (cpf) => set({ cpf }),
  setBirthDate: (birthDate) => set({ birthDate }),
  setResponsibleName: (responsibleName) => set({ responsibleName }),
  setCEP: (cep) => set({ cep }),
  setCity: (city) => set({ city }),
  setNumber: (number) => set({ number }),
  setCNPJ: (cnpj) => set({ cnpj }),
  setCompanyName: (companyName) => set({ companyName }),
  setUF: (uf) => set({ uf }),
  setPhoneNumber: (phoneNumber) => set({ phoneNumber }),
  setStatus: (status) => set({ status }),
  setNewPat: (newPat) => set({ newPat }),
  setResponsiblePhoneNumber: (responsiblePhoneNumber) =>
    set({ responsiblePhoneNumber }),
  setAddress: (address) => set({ address }),
  setAddressLine2: (addressLine2) => set({ addressLine2 }),
  setNeighborhood: (neighborhood) => set({ neighborhood }),
  setCellphone: (cellphone) => set({ cellphone }),
  setHomeVoltage: (homeVoltage) => set({ homeVoltage }),
  setDiagnosisHypothesis: (diagnosisHypothesis) => set({ diagnosisHypothesis }),
  setOnPaymentList: (onPaymentList) => set({ onPaymentList }),

  setKpiSelectedPatient: (patient) => {
    if (patient === null) {
      set({ kpiSelectedPatient: null });
    } else {
      set({ kpiSelectedPatient: { ...patient } });
    }
  },

  setSearchTerm: (searchTerm) => set({ searchTerm }),
  setPatientAutofilledFromDatalake: (patientAutofilledFromDatalake) =>
    set({ patientAutofilledFromDatalake }),

  clearPatientsList: () => {
    latestPatientsRequest += 1;
    set({
      patients: [],
      fetchTotalItems: 0,
      error: null,
      infoMessage: null,
    });
  },

  fetchPatients: async (cnpj, filter, page, pageSize, status) => {
    const requestId = ++latestPatientsRequest;
    const value = await getPatients(cnpj, filter, page, pageSize, status);
    if (requestId !== latestPatientsRequest) {
      return value;
    }

    const visiblePatients = (value.data ?? []).filter(
      (patient) => !isUnregisteredPortalHomecarePatient(patient)
    );
    const hasUnregisteredPatient = Boolean(
      value.hasUnregisteredDatalakePatient
    );
    const isExplicitSearch = String(filter ?? '').trim() !== '';
    const onlyUnregisteredPatients =
      isExplicitSearch &&
      hasUnregisteredPatient &&
      visiblePatients.length === 0;

    set({
      patients: [...visiblePatients],
      fetchTotalItems: onlyUnregisteredPatients ? 0 : value.rows ?? 0,
      error: value.error,
      infoMessage: onlyUnregisteredPatients
        ? UNREGISTERED_PATIENT_MESSAGE
        : null,
    });
    return value;
  },

  fetchPatientBySapSearch: async (cnpj, sap) => {
    const normalizedSap = String(sap ?? '').replace(/\D/g, '');

    if (!normalizedSap) {
      return {
        httpStatus: 400,
        body: {
          message: 'Código SAP deve ser informado.',
          data: [],
        },
      };
    }

    try {
      const value = await getPatients(cnpj, normalizedSap, 0, 10, null);

      return {
        httpStatus: value?.httpStatus ?? 200,
        body: {
          ...value,
          data: Array.isArray(value?.data) ? value.data : [],
        },
      };
    } catch (error) {
      return {
        httpStatus: error?.response?.status ?? 500,
        body: {
          message:
            error?.response?.data?.message ??
            error?.message ??
            'Erro ao buscar paciente pelo SAP.',
          data: [],
        },
      };
    }
  },

  insertPatient: async () => {
    const state = get();
    const sap = normalizeSap(state.sap);
    const cpf = isValidCpf(state.cpf)
      ? onlyDigits(state.cpf)
      : isEnterpriseSap(sap)
      ? '00000000000'
      : onlyDigits(state.cpf);
    const status = normalizePatientStatusForSap(state.status, sap);

    const validationErrors = validatePatientBeforeSave(state);

    if (validationErrors.length > 0) {
      return {
        httpStatus: 400,
        body: validationErrors,
      };
    }

    const newPatient = {
      ...(state.newPat ? {} : { id: state.id }),
      name: state.name,
      email: state.email,
      sap,
      birth_date: state.birthDate,
      cpf,
      cnpj: onlyDigits(state.cnpj),
      companyName: state.companyName,
      cep: onlyDigits(state.cep),
      city: state.city,
      uf: state.uf,
      number: state.number,
      address: state.address,
      addressLine2: state.addressLine2,
      delivery_neighborhood: state.neighborhood,
      cellphone: onlyDigits(state.cellphone),
      cellphone2: onlyDigits(state.phoneNumber),
      status,
      on_payment_list: state.onPaymentList,
      additionalInformation: state.additionalInformation,
    };

    return await savePatient(newPatient);
  },

  getSelectedPatientRechargeQuota: async (cnpj) => {
    const state = get();
    const cpf = onlyDigits(state.currentSelectPatientCPF);
    const sap = normalizeSap(state.currentSelectPatientSap);
    return await getPatientRechargeQuota({ cnpj, sap, cpf });
  },

  deactivatePatient: async () => {
    const state = get();
    const cpf = onlyDigits(state.currentSelectPatientCPF);
    const sap = normalizeSap(state.currentSelectPatientSap);
    const resp = await deactivatePatient({ sap, cpf });
    return resp;
  },

  getCEP: async (cep) => {
    const resp = await getCEP(cep);
    return resp;
  },
}));
